/** Shared clicker state merge — server spends win; pending taps may keep a bounded local lead. */

const num = (v, fallback = 0) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};

const hasOwn = (obj, key) =>
  obj != null && Object.prototype.hasOwnProperty.call(obj, key);

const TAPS_KEY = 'sync_taps';
const INFLIGHT_KEY = 'sync_taps_inflight';

/** LS pending + in-flight (mirrors getOutstandingTaps without importing tapFlush). */
function readOutstandingTaps() {
  try {
    const pending = parseInt(localStorage.getItem(TAPS_KEY), 10) || 0;
    const inflight = parseInt(localStorage.getItem(INFLIGHT_KEY), 10) || 0;
    return Math.max(0, pending) + Math.max(0, inflight);
  } catch (_) {
    return 0;
  }
}

/**
 * Prefer explicit synced/extra values — including null — so claim flows can
 * clear offline / engagement flags.
 */
function pickNullable(synced, extra, prev, key) {
  if (hasOwn(extra, key)) return extra[key];
  if (hasOwn(synced, key)) return synced[key];
  return prev?.[key];
}

/**
 * Balance shown in UI: server balance + unflushed taps (same formula as Main).
 * No sticky floor — sticky peaks caused jump-up/jump-down on tab switches.
 */
export function balanceWithPending(clicker) {
  const bal = num(clicker?.balance, 0);
  const ppt = num(clicker?.coin_per_click, 0.01);
  const pMult = num(clicker?.prestige_multiplier, 1) || 1;
  return bal + readOutstandingTaps() * ppt * pMult;
}

/** @deprecated kept for callers that reset after spends — no-op now */
export function resetPendingDisplayFloor() {
  /* no sticky floor */
}

/**
 * Merge a server clicker payload into previous React state.
 */
export function mergeClickerState(prev, synced, extra = {}) {
  if (!synced || typeof synced !== 'object') {
    return prev ? { ...prev, ...extra } : extra;
  }
  const { preferRemoteBalance, forceBalance, ...restExtra } = extra || {};
  const base = { ...(prev || {}), ...synced, ...restExtra };

  const prevTs = num(prev?.amber_updated_at, 0);
  const nextTs = num(synced.amber_updated_at, 0);
  if (prev && synced.amber != null && prevTs > nextTs) {
    base.amber = prev.amber;
    base.amber_updated_at = prev.amber_updated_at;
    if (prev.amber_lifetime != null) base.amber_lifetime = prev.amber_lifetime;
  }

  base.engagement = pickNullable(synced, restExtra, prev, 'engagement');
  base.offline = pickNullable(synced, restExtra, prev, 'offline');
  if (hasOwn(synced, 'season') && synced.season) {
    base.season = synced.season;
  } else if (hasOwn(restExtra, 'season') && restExtra.season) {
    base.season = restExtra.season;
  } else if (!base.season) {
    base.season = prev?.season;
  }

  const localBal = num(prev?.balance, NaN);
  const remoteBal = num(synced.balance, NaN);
  const forceRemote = preferRemoteBalance === true || forceBalance === true;
  const outstanding = readOutstandingTaps();
  const ppt = num(synced.coin_per_click ?? prev?.coin_per_click, 0.01);
  const pMult = num(synced.prestige_multiplier ?? prev?.prestige_multiplier, 1) || 1;
  const maxLead = outstanding > 0 ? outstanding * ppt * pMult + 0.05 : 0;

  if (Number.isFinite(remoteBal)) {
    if (forceRemote) {
      if (
        outstanding > 0 &&
        Number.isFinite(localBal) &&
        localBal > remoteBal &&
        localBal - remoteBal <= maxLead
      ) {
        base.balance = String(localBal);
      } else {
        base.balance = String(remoteBal);
      }
    } else if (Number.isFinite(localBal) && localBal > remoteBal) {
      if (outstanding > 0 && localBal - remoteBal <= maxLead) {
        base.balance = String(localBal);
      }
    }
  }

  // Energy while taps outstanding: never raise to raw remote, never subtract
  // outstanding from an already-optimistic local (that double-debits to 0).
  // Keep local spend/regen; only derive from remote when local is missing.
  const localEn = num(prev?.energy, NaN);
  const remoteEn = num(synced.energy, NaN);
  if (Number.isFinite(remoteEn)) {
    if (outstanding > 0) {
      if (Number.isFinite(localEn)) {
        // Allow local to stay below remote (optimistic spend / regen after empty).
        // Never jump UP to remote while the queue still owes spend.
        if (localEn > remoteEn + 1e-6) {
          base.energy = String(remoteEn);
        } else {
          base.energy = String(Math.max(0, localEn));
        }
      } else {
        base.energy = String(Math.max(0, remoteEn - outstanding * ppt));
      }
    } else {
      base.energy = String(remoteEn);
    }
  }

  if (!hasOwn(synced, 'coin_per_hour') && prev?.coin_per_hour != null) {
    base.coin_per_hour = prev.coin_per_hour;
  }

  // Keep BP snapshot across light tap payloads that omit it
  if (!hasOwn(synced, 'battle_pass') && prev?.battle_pass) {
    base.battle_pass = prev.battle_pass;
  }

  return base;
}

export function parseMoney(v, fallback = 0) {
  return num(v, fallback);
}
